import { DestroyRef, inject, Injectable, Injector, signal, Signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { filter, isObservable, Subject, takeUntil } from 'rxjs';

import type { BreadcrumbLabelSource, BreadcrumbRouteData } from './breadcrumb-label.contract';

export interface BreadcrumbSegment {
  readonly label: string;
  readonly url: string;
  readonly isCurrent: boolean;
}

/**
 * Recomputes breadcrumb segments from the activated-route tree on
 * NavigationEnd (after the destination has rendered — ResolveFn is never
 * used, FR-029), seeds every segment with its declared static label, then
 * upgrades via the route's label source. Rejection, error, empty,
 * whitespace-only, and never-resolving all leave the static label permanently
 * (FR-030); a resolution arriving after the user navigates away is discarded
 * (navigation-id check); a throwing source is swallowed without console
 * output (FR-042).
 */
@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  private navigationId = 0;
  private readonly cancelLabelSources = new Subject<void>();
  private readonly segmentsState = signal<readonly BreadcrumbSegment[]>([]);
  readonly segments: Signal<readonly BreadcrumbSegment[]> = this.segmentsState.asReadonly();

  constructor() {
    this.recompute();
    this.destroyRef.onDestroy(() => {
      this.cancelLabelSources.next();
      this.cancelLabelSources.complete();
    });
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.recompute());
  }

  private recompute(): void {
    this.cancelLabelSources.next();
    const navigationId = ++this.navigationId;
    const segments: BreadcrumbSegment[] = [];
    const upgrades: { index: number; source: BreadcrumbLabelSource; params: Params }[] = [];

    let url = '';
    let route: ActivatedRoute | null = this.router.routerState.root.firstChild;
    while (route) {
      const data = route.routeConfig?.data?.['breadcrumb'] as BreadcrumbRouteData | undefined;
      if (data) {
        url += '/' + route.snapshot.url.map((segment) => segment.path).join('/');
        segments.push({ label: data.label, url, isCurrent: false });
        if (data.labelSource) {
          // Optional lookup: an unprovided token degrades to the static label
          // rather than breaking the breadcrumb or navigation (rule 7).
          const source = this.injector.get(data.labelSource, null);
          if (source) {
            upgrades.push({
              index: segments.length - 1,
              source,
              params: route.snapshot.params,
            });
          }
        }
      }
      route = route.firstChild;
    }

    const last = segments.length - 1;
    if (last >= 0) {
      segments[last] = { ...segments[last], isCurrent: true };
    }
    this.segmentsState.set(segments);

    // Post-activation upgrades — navigation is already complete (FR-029).
    for (const upgrade of upgrades) {
      this.upgradeLabel(navigationId, upgrade.index, upgrade.source, upgrade.params);
    }
  }

  private upgradeLabel(
    navigationId: number,
    index: number,
    source: BreadcrumbLabelSource,
    params: Params,
  ): void {
    let resolved: ReturnType<BreadcrumbLabelSource['resolveLabel']>;
    try {
      resolved = source.resolveLabel(params);
    } catch {
      return; // throwing source: permanent fallback, no console output (rule 7)
    }

    if (isObservable(resolved)) {
      resolved.pipe(takeUntil(this.cancelLabelSources), takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (value) => this.applyIfFresh(navigationId, index, value),
        error: () => {
          // permanent fallback (rules 4, 7) — swallowed, never console.error
        },
      });
    } else {
      resolved
        .then((value) => this.applyIfFresh(navigationId, index, value))
        .catch(() => {
          // permanent fallback (rule 4) — swallowed
        });
    }
  }

  private applyIfFresh(navigationId: number, index: number, value: string): void {
    if (navigationId !== this.navigationId) return; // navigated away — discard
    if (typeof value !== 'string' || value.trim() === '') return; // permanent fallback

    this.segmentsState.update((segments) =>
      segments.map((segment, i) => (i === index ? { ...segment, label: value } : segment)),
    );
  }
}
